import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axios from "axios";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import DownloadIcon from "@mui/icons-material/Download";
import SchoolIcon from "@mui/icons-material/School";
import PaymentsIcon from "@mui/icons-material/Payments";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { API_URL } from "./config";

const ORANGE = "#F07828";
const ORANGE_2 = "#ed7f35";
const BLUE = "rgba(25,118,210,0.95)";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";

const cardSx = {
  borderRadius: 2,
  border: `1px solid ${BORDER}`,
  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  overflow: "hidden",
  bgcolor: "#fff",
};

const sectionSx = {
  ...cardSx,
  p: 2.5,
  position: "relative",
  breakInside: "avoid",

  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    background: `linear-gradient(
      180deg,
      ${ORANGE} 0%,
      ${ORANGE_2} 100%
    )`,
  },
};

const inputSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: BORDER,
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(25,118,210,0.45)",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: ORANGE,
  },
};

const headCellSx = {
  fontWeight: 900,
  bgcolor: BLUE,
  color: "#fff",
  whiteSpace: "nowrap",
  py: 1,
};

const cellSx = {
  whiteSpace: "nowrap",
  fontSize: 12,
  py: 0.8,
};

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function displayValue(value) {
  return value === null ||
    value === undefined ||
    value === ""
    ? "-"
    : value;
}

function pct(value) {
  if (value === null || value === undefined) {
    return "-";
  }

  return `${Number(value).toFixed(2)}%`;
}

function dateOnly(value) {
  if (!value) return "-";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function normalizeExternalUrl(url) {
  if (!url) return null;

  const trimmed = String(url).trim();

  if (!trimmed) return null;

  // Already a complete external URL.
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Prevent the browser from turning it into:
  // http://localhost:3000/example.com
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function waitForRender(delay = 100) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, delay);
      });
    });
  });
}

function StatusChip({ status }) {
  const normalized = String(
    status || "-"
  ).toUpperCase();

  let color = "default";

  if (
    ["PAID", "APPROVED", "EVALUATED"].includes(
      normalized
    )
  ) {
    color = "success";
  } else if (
    ["PARTIAL", "SUBMITTED"].includes(normalized)
  ) {
    color = "warning";
  } else if (
    ["DUE", "REJECTED"].includes(normalized)
  ) {
    color = "error";
  }

  return (
    <Chip
      size="small"
      label={normalized}
      color={color}
      variant="outlined"
    />
  );
}

function StatCard({
  icon,
  title,
  value,
  subtitle,
}) {
  return (
    <Paper sx={{ ...cardSx, p: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: "rgba(25,118,210,0.08)",
            color: "#1d4ed8",
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              color: "#0f172a",
            }}
          >
            {value}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function SectionTitle({
  title,
  subtitle,
}) {
  return (
    <Box sx={{ mb: 2, pl: 1 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 900,
          color: ORANGE,
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function ReportContent({ report }) {
  const marksBySemester = useMemo(() => {
    return (report?.marks || []).reduce(
      (accumulator, row) => {
        const semester = row.Semester || "NA";

        if (!accumulator[semester]) {
          accumulator[semester] = [];
        }

        accumulator[semester].push(row);

        return accumulator;
      },
      {}
    );
  }, [report]);

  if (!report) return null;

  return (
    <Box
      className="student-progress-report"
      sx={{
        width: 1120,
        bgcolor: "#fff",
        p: 2,
        color: "#111827",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <Paper
        sx={{
          ...cardSx,
          p: 3,
          mb: 2,
          background:
            "linear-gradient(135deg, #eff6ff 0%, #ffffff 55%, #fff7ed 100%)",
          borderTop: `5px solid ${ORANGE}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 950,
                color: "#172554",
              }}
            >
              {report.student.FullName}
            </Typography>

            <Typography
              sx={{
                color: "#475569",
                fontWeight: 800,
              }}
            >
              {report.student.StudentID}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ mt: 1 }}
            >
              <Chip
                label={displayValue(
                  report.student.CampusCode
                )}
                size="small"
              />

              <Chip
                label={displayValue(
                  report.student.SubCampusCode
                )}
                size="small"
              />

              <Chip
                label={displayValue(
                  report.student.DegreeCode
                )}
                size="small"
              />

              <Chip
                label={displayValue(
                  report.student.BranchCode
                )}
                size="small"
              />

              <Chip
                label={`Sem ${displayValue(
                  report.student.Semester
                )}`}
                size="small"
              />

              <Chip
                label={`Sec ${displayValue(
                  report.student.Section
                )}`}
                size="small"
              />
            </Stack>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Academic Year
            </Typography>

            <Typography
              variant="h6"
              sx={{ fontWeight: 900 }}
            >
              {displayValue(
                report.student.AcademicYear
              )}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Regulation:{" "}
              {displayValue(
                report.student.Regulation
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Grid
        container
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Grid item xs={3}>
          <StatCard
            icon={<TrendingUpIcon />}
            title="Attendance"
            value={pct(
              report.summary?.attendance
                ?.AttendancePercentage
            )}
            subtitle={`Rank ${displayValue(
              report.summary?.attendance
                ?.AttendanceRank
            )} / ${displayValue(
              report.summary?.attendance
                ?.TotalPeers
            )}`}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            icon={<SchoolIcon />}
            title="CGPA"
            value={displayValue(
              report.summary?.cgpa
            )}
            subtitle={`Backlogs: ${displayValue(
              report.summary?.backlogCount
            )}`}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            icon={<PaymentsIcon />}
            title="Fee Due"
            value={money(
              report.summary?.totalFeeDue
            )}
            subtitle={`Paid: ${money(
              report.summary?.totalFeePaid
            )}`}
          />
        </Grid>

        <Grid item xs={3}>
          <StatCard
            icon={<EventBusyIcon />}
            title="Leave Days"
            value={displayValue(
              report.summary?.totalLeaveDays
            )}
            subtitle={`Projects: ${displayValue(
              report.summary?.projectCount
            )}`}
          />
        </Grid>
      </Grid>

      <Paper sx={sectionSx}>
        <SectionTitle title="Attendance Summary" />

        <Grid
          container
          spacing={1.5}
          sx={{ pl: 1 }}
        >
          {[
            [
              "Total Sessions",
              report.attendance?.TotalSessions,
            ],
            [
              "Present",
              report.attendance?.PresentSessions,
            ],
            [
              "Absent",
              report.attendance?.AbsentSessions,
            ],
            [
              "Percentage",
              pct(
                report.attendance
                  ?.AttendancePercentage
              ),
            ],
            [
              "Class Average",
              pct(
                report.attendance
                  ?.ClassAverageAttendance
              ),
            ],
            [
              "Highest",
              pct(
                report.attendance
                  ?.HighestAttendance
              ),
            ],
            [
              "Lowest",
              pct(
                report.attendance
                  ?.LowestAttendance
              ),
            ],
            [
              "Rank",
              `${displayValue(
                report.attendance?.AttendanceRank
              )} / ${displayValue(
                report.attendance?.TotalPeers
              )}`,
            ],
          ].map(([label, value]) => (
            <Grid item xs={3} key={label}>
              <Box
                sx={{
                  p: 1.3,
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {label}
                </Typography>

                <Typography
                  sx={{ fontWeight: 900 }}
                >
                  {displayValue(value)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Marks & Academic Performance" />

        {Object.keys(marksBySemester).length ===
        0 ? (
          <Alert severity="info">
            No marks found.
          </Alert>
        ) : (
          Object.entries(
            marksBySemester
          ).map(([semester, rows]) => (
            <Box
              key={semester}
              sx={{
                mb: 2.5,
                pl: 1,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "#1e3a8a",
                  }}
                >
                  Semester {semester}
                </Typography>

                <Chip
                  size="small"
                  label={`GPA ${displayValue(
                    rows[0]?.SemesterGPA
                  )} | Rank ${displayValue(
                    rows[0]?.SemesterGPARank
                  )} / ${displayValue(
                    rows[0]?.TotalPeersInSemester
                  )}`}
                />
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>
                      Subject
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Grade
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Point
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Subject Rank
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Class Avg
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Highest
                    </TableCell>

                    <TableCell sx={headCellSx}>
                      Lowest
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((mark, index) => (
                    <TableRow
                      key={`${mark.SubjectCode}-${index}`}
                    >
                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectName
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(mark.Grade)}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.GradePoint
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectRank
                        )}{" "}
                        /{" "}
                        {displayValue(
                          mark.TotalPeersInSubject
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectClassAverageGradePoint
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectHighestGradePoint
                        )}
                      </TableCell>

                      <TableCell sx={cellSx}>
                        {displayValue(
                          mark.SubjectLowestGradePoint
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))
        )}
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Fee Details" />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>
                Academic Year
              </TableCell>

              <TableCell sx={headCellSx}>
                Sem
              </TableCell>

              <TableCell sx={headCellSx}>
                Fee
              </TableCell>

              <TableCell sx={headCellSx}>
                Amount
              </TableCell>

              <TableCell sx={headCellSx}>
                Paid
              </TableCell>

              <TableCell sx={headCellSx}>
                Due
              </TableCell>

              <TableCell sx={headCellSx}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(report.fees || []).map(
              (fee, index) => (
                <TableRow key={index}>
                  <TableCell sx={cellSx}>
                    {displayValue(
                      fee.AcademicYear
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(fee.Semester)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(fee.FeeLabel)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {money(fee.FeeAmount)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {money(fee.FeePaid)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {money(fee.FeeDue)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <StatusChip
                      status={fee.PaymentStatus}
                    />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Academic Projects" />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>
                Academic Year
              </TableCell>

              <TableCell sx={headCellSx}>
                Subjects
              </TableCell>

              <TableCell sx={headCellSx}>
                Project
              </TableCell>

              <TableCell sx={headCellSx}>
                Marks
              </TableCell>

              <TableCell sx={headCellSx}>
                Links
              </TableCell>

              <TableCell sx={headCellSx}>
                Remarks
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(report.projects || []).map(
              (project, index) => (
                <TableRow key={index}>
                  <TableCell sx={cellSx}>
                    {displayValue(
                      project.AcademicYear
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      project.Subjects
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      project.ProjectTitle
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(project.Marks)}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <Stack
                      direction="row"
                      spacing={0.6}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {project.GitHubUrl && (
                        <Chip
                          size="small"
                          label="GitHub"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.GitHubUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.GitHubUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#f8fafc",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {project.YouTubeUrl && (
                        <Chip
                          size="small"
                          label="Video"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.YouTubeUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.YouTubeUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#fef2f2",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {project.NotionPromptUrl && (
                        <Chip
                          size="small"
                          label="Notion"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.NotionPromptUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.NotionPromptUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#f8fafc",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {project.DeploymentUrl && (
                        <Chip
                          size="small"
                          label="Live"
                          clickable
                          component="a"
                          href={normalizeExternalUrl(
                            project.DeploymentUrl
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pdf-url={normalizeExternalUrl(
                            project.DeploymentUrl
                          )}
                          sx={{
                            fontWeight: 800,
                            bgcolor: "#ecfdf5",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      )}
                  
                      {!project.GitHubUrl &&
                        !project.YouTubeUrl &&
                        !project.NotionPromptUrl &&
                        !project.DeploymentUrl && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            -
                          </Typography>
                        )}
                    </Stack>
                  </TableCell>

                  <TableCell
                    sx={{
                      ...cellSx,
                      whiteSpace: "normal",
                    }}
                  >
                    {displayValue(
                      project.TeacherRemarks
                    )}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Time Off Requests" />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>
                Academic Year
              </TableCell>

              <TableCell sx={headCellSx}>
                Leave Type
              </TableCell>

              <TableCell sx={headCellSx}>
                Start
              </TableCell>

              <TableCell sx={headCellSx}>
                End
              </TableCell>

              <TableCell sx={headCellSx}>
                Days
              </TableCell>

              <TableCell sx={headCellSx}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(report.timeOff || []).map(
              (timeOff, index) => (
                <TableRow key={index}>
                  <TableCell sx={cellSx}>
                    {displayValue(
                      timeOff.AcademicYear
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      timeOff.LeaveType
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {dateOnly(
                      timeOff.LeaveStartDate
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {dateOnly(
                      timeOff.LeaveEndDate
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {displayValue(
                      timeOff.Duration
                    )}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <StatusChip
                      status={timeOff.Status}
                    />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper
        sx={{
          ...sectionSx,
          mt: 2,
        }}
      >
        <SectionTitle title="Events & Assignments" />

        <Stack
          direction="row"
          spacing={1}
          sx={{ pl: 1 }}
        >
          <Chip
            icon={<AssignmentTurnedInIcon />}
            label={`Assignments: ${
              (report.assignments || []).length
            }`}
          />

          <Chip
            label={`Events: ${
              (report.events || []).length
            }`}
          />
        </Stack>
      </Paper>

      <Divider sx={{ my: 2.5 }} />

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: "text.secondary",
        }}
      >
        Generated by UniFLOO Student Progress Report
      </Typography>
    </Box>
  );
}

export default function StudentDetailReport() {
  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const pdfStageRef = useRef(null);

  const [report, setReport] = useState(null);
  const [pdfReport, setPdfReport] = useState(null);

  const [loadingReport, setLoadingReport] =
    useState(true);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [error, setError] = useState("");

  const token =
    localStorage.getItem("auth") ||
    localStorage.getItem("token");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  /*
   * Decode a JWT payload safely.
   *
   * JWT uses Base64URL, so "-" and "_"
   * must be converted before calling atob().
   */
  const tokenPayload = useMemo(() => {
    if (!token) return null;

    try {
      const tokenParts = token.split(".");

      if (tokenParts.length !== 3) {
        throw new Error(
          "Invalid authentication token"
        );
      }

      let payload = tokenParts[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/");

      while (payload.length % 4) {
        payload += "=";
      }

      return JSON.parse(atob(payload));
    } catch (error) {
      console.error(
        "Failed to decode login token:",
        error
      );

      return null;
    }
  }, [token]);

  /*
   * Support the different field-name variants
   * that may currently exist in your login token.
   */
  const loggedInStudent = useMemo(() => {
    return {
      StudentKey:
        tokenPayload?.studentkey ??
        tokenPayload?.studentKey ??
        tokenPayload?.StudentKey ??
        tokenPayload?.student_key ??
        null,

      StudentID:
        tokenPayload?.studentid ??
        tokenPayload?.studentId ??
        tokenPayload?.StudentID ??
        tokenPayload?.student_id ??
        null,

      CampusCode:
        tokenPayload?.campusCode ??
        tokenPayload?.CampusCode ??
        tokenPayload?.campuscode ??
        null,
    };
  }, [tokenPayload]);

  /*
   * StudentKey and StudentID are both sent
   * from the authenticated login token.
   */
  const fetchLoggedInStudentReport =
    async () => {
      if (!token) {
        throw new Error(
          "Authentication token is missing. Please log in again."
        );
      }

      if (
        !loggedInStudent.StudentKey &&
        !loggedInStudent.StudentID
      ) {
        throw new Error(
          "StudentKey and StudentID are missing from the login token."
        );
      }

      const response = await axios.get(
        `${API_URL}/api/studentDetailReport/reports/student-detail`,
        {
          headers: authHeaders,

          params: {
            StudentKey:
              loggedInStudent.StudentKey ||
              undefined,

            StudentID:
              loggedInStudent.StudentID ||
              undefined,
          },
        }
      );

      return response.data;
    };

  /*
   * Automatically load the logged-in student's
   * report when the page opens.
   */
  useEffect(() => {
    let cancelled = false;

    const loadReport = async () => {
      try {
        setLoadingReport(true);
        setError("");

        const reportData =
          await fetchLoggedInStudentReport();

        if (!cancelled) {
          setReport(reportData);
        }
      } catch (error) {
        console.error(
          "Student report error:",
          error
        );

        if (!cancelled) {
          setError(
            error.response?.data?.error ||
              error.response?.data?.message ||
              error.message ||
              "Failed to load student report"
          );

          setReport(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingReport(false);
        }
      }
    };

    loadReport();

    return () => {
      cancelled = true;
    };

    // authHeaders is derived from token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    token,
    loggedInStudent.StudentKey,
    loggedInStudent.StudentID,
  ]);

  const addCanvasToPdf = (
    pdf,
    sourceCanvas,
    reportNode
  ) => {
    const pageWidthMm =
      pdf.internal.pageSize.getWidth();

    const pageHeightMm =
      pdf.internal.pageSize.getHeight();

    const pixelsPerMm =
      sourceCanvas.width / pageWidthMm;

    const pageHeightPx = Math.floor(
      pageHeightMm * pixelsPerMm
    );

    const reportRect =
      reportNode.getBoundingClientRect();

    const canvasScaleX =
      sourceCanvas.width /
      reportRect.width;

    const canvasScaleY =
      sourceCanvas.height /
      reportRect.height;

    /*
     * Find the project links so they can remain
     * clickable inside the generated PDF.
     */
    const pdfLinks = Array.from(
      reportNode.querySelectorAll(
        "[data-pdf-url]"
      )
    )
      .map((element) => {
        const url =
          element.getAttribute(
            "data-pdf-url"
          );

        const rect =
          element.getBoundingClientRect();

        if (!url) return null;

        return {
          url,

          x:
            (rect.left -
              reportRect.left) *
            canvasScaleX,

          y:
            (rect.top -
              reportRect.top) *
            canvasScaleY,

          width:
            rect.width *
            canvasScaleX,

          height:
            rect.height *
            canvasScaleY,
        };
      })
      .filter(Boolean);

    let sourceY = 0;
    let pageIndex = 0;

    while (
      sourceY < sourceCanvas.height
    ) {
      const sliceHeight = Math.min(
        pageHeightPx,
        sourceCanvas.height - sourceY
      );

      const pageCanvas =
        document.createElement("canvas");

      pageCanvas.width =
        sourceCanvas.width;

      pageCanvas.height =
        sliceHeight;

      const context =
        pageCanvas.getContext("2d", {
          alpha: false,
        });

      if (!context) {
        throw new Error(
          "Could not create PDF canvas context"
        );
      }

      context.fillStyle = "#ffffff";

      context.fillRect(
        0,
        0,
        pageCanvas.width,
        pageCanvas.height
      );

      context.drawImage(
        sourceCanvas,
        0,
        sourceY,
        sourceCanvas.width,
        sliceHeight,
        0,
        0,
        sourceCanvas.width,
        sliceHeight
      );

      if (pageIndex > 0) {
        pdf.addPage();
      }

      const imageData =
        pageCanvas.toDataURL(
          "image/jpeg",
          0.68
        );

      const renderedHeightMm =
        sliceHeight / pixelsPerMm;

      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pageWidthMm,
        renderedHeightMm,
        undefined,
        "FAST"
      );

      pdfLinks.forEach((link) => {
        const linkTop = link.y;

        const linkBottom =
          link.y + link.height;

        const pageTop = sourceY;

        const pageBottom =
          sourceY + sliceHeight;

        const intersectsPage =
          linkBottom > pageTop &&
          linkTop < pageBottom;

        if (!intersectsPage) return;

        const clippedTop = Math.max(
          linkTop,
          pageTop
        );

        const clippedBottom = Math.min(
          linkBottom,
          pageBottom
        );

        const xMm =
          link.x / pixelsPerMm;

        const yMm =
          (clippedTop - pageTop) /
          pixelsPerMm;

        const widthMm =
          link.width / pixelsPerMm;

        const heightMm =
          (clippedBottom - clippedTop) /
          pixelsPerMm;

        pdf.link(
          xMm,
          yMm,
          widthMm,
          heightMm,
          {
            url: link.url,
          }
        );
      });

      sourceY += sliceHeight;
      pageIndex += 1;
    }
  };

  const downloadPdf = async () => {
    if (!report) {
      setError(
        "The student report is not available."
      );

      return;
    }

    try {
      setPdfLoading(true);
      setError("");

      /*
       * Render the currently loaded report in the
       * hidden fixed-width PDF staging area.
       */
      setPdfReport(report);

      await waitForRender(150);

      const reportNode =
        pdfStageRef.current?.querySelector(
          ".student-progress-report"
        );

      if (!reportNode) {
        throw new Error(
          "Could not render the student report."
        );
      }

      const canvas = await html2canvas(
        reportNode,
        {
          scale: 1.15,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          imageTimeout: 15000,
          removeContainer: true,
        }
      );

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
        putOnlyUsedFonts: true,
      });

      addCanvasToPdf(
        pdf,
        canvas,
        reportNode
      );

      const studentId =
        report?.student?.StudentID ||
        loggedInStudent.StudentID ||
        loggedInStudent.StudentKey ||
        "Student";

      pdf.save(
        `Student_Progress_Report_${studentId}.pdf`
      );
    } catch (error) {
      console.error(
        "PDF generation error:",
        error
      );

      setError(
        error.message ||
          "Failed to generate the PDF"
      );
    } finally {
      setPdfLoading(false);
      setPdfReport(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: BG,

        p: {
          xs: 1.5,
          md: 2,
        },
      }}
    >
      <Paper
        sx={{
          ...cardSx,
          mb: 2,
        }}
      >
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },

            py: {
              xs: 2,
              md: 2.5,
            },

            background: `linear-gradient(
              100deg,
              ${ORANGE} 0%,
              ${ORANGE_2} 55%,
              #f59e0b 100%
            )`,

            color: "#fff",
          }}
        >
          <Stack
            direction={
              isMobile
                ? "column"
                : "row"
            }
            justifyContent="space-between"
            alignItems={
              isMobile
                ? "stretch"
                : "center"
            }
            spacing={1.5}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: {
                    xs: "1.25rem",
                    md: "1.5rem",
                  },

                  fontWeight: 950,
                  lineHeight: 1.2,
                }}
              >
                My Student Progress Report
              </Typography>

              <Typography
                sx={{
                  opacity: 0.92,
                  mt: 0.5,
                  fontSize: "0.9rem",
                }}
              >
                Your report is loaded automatically
                using your authenticated student
                account.
              </Typography>
            </Box>

            {report && (
              <Stack
                direction={
                  isMobile
                    ? "column"
                    : "row"
                }
                spacing={1}
                alignItems={
                  isMobile
                    ? "stretch"
                    : "center"
                }
              >
                
                <Button
                  variant="contained"
                  startIcon={
                    pdfLoading ? (
                      <CircularProgress
                        size={16}
                        sx={{
                          color: "#fff",
                        }}
                      />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={downloadPdf}
                  disabled={pdfLoading}
                  sx={{
                    bgcolor: "#fff",
                    color: ORANGE,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 900,

                    "&:hover": {
                      bgcolor:
                        "rgba(255,255,255,0.90)",
                    },

                    "&.Mui-disabled": {
                      bgcolor:
                        "rgba(255,255,255,0.45)",
                      color:
                        "rgba(240,120,40,0.75)",
                    },
                  }}
                >
                  {pdfLoading
                    ? "Generating PDF..."
                    : "Download PDF"}
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>
      </Paper>

      {loadingReport && (
        <Paper
          sx={{
            ...cardSx,
            p: 5,
            textAlign: "center",
          }}
        >
          <CircularProgress />

          <Typography
            sx={{
              mt: 1.5,
              fontWeight: 900,
              color: "#172554",
            }}
          >
            Loading your progress report...
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Student information is being read
            from your login session.
          </Typography>
        </Paper>
      )}

      {error && !loadingReport && (
        <Alert
          severity="error"
          sx={{
            borderRadius: 2,
            mb: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {report && !loadingReport && (
        <Box
          sx={{
            overflowX: "auto",
          }}
        >
          <ReportContent report={report} />
        </Box>
      )}

      {/*
       * PDF staging area.
       *
       * Do not use display:none because html2canvas
       * cannot measure hidden content.
       */}
      <Box
        ref={pdfStageRef}
        aria-hidden="true"
        sx={{
          position: "fixed",
          left: "-20000px",
          top: 0,
          width: 1120,
          bgcolor: "#fff",
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        {pdfReport && (
          <ReportContent
            key={
              pdfReport?.student
                ?.StudentKey ||
              pdfReport?.student?.StudentID
            }
            report={pdfReport}
          />
        )}
      </Box>
    </Box>
  );
}